import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { CreditCard, Calendar, Send, Loader2, Mail, AlertCircle, ExternalLink, RefreshCw } from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Pagos = () => {
    const { canEdit } = useAuth();
    const [loading, setLoading] = useState(true);
    const [pagos, setPagos] = useState([]);
    const [recordatorios, setRecordatorios] = useState([]);
    const [diasConfig, setDiasConfig] = useState([]);
    const [loadingRecord, setLoadingRecord] = useState(true);
    const [enviandoId, setEnviandoId] = useState(null);

    useEffect(() => {
        fetchPagos();
        fetchRecordatorios();
    }, []);

    const fetchPagos = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/api/pagos`);
            setPagos(response.data);
        } catch (error) {
            console.error('Error fetching pagos:', error);
            toast.error('Error al cargar pagos');
        } finally {
            setLoading(false);
        }
    };

    const fetchRecordatorios = async () => {
        try {
            setLoadingRecord(true);
            const response = await axios.get(`${API_URL}/api/recordatorios/pendientes`);
            setRecordatorios(response.data.items || []);
            setDiasConfig(response.data.dias_configurados || []);
        } catch (error) {
            console.error('Error fetching recordatorios:', error);
            // Silent — endpoint may fail if no empresa
        } finally {
            setLoadingRecord(false);
        }
    };

    const handleEnviarRecordatorio = async (item) => {
        if (!item.tiene_email) {
            const email = window.prompt(
                `El cliente "${item.cliente_nombre}" no tiene email de cobranza configurado.\n¿A qué correo enviar el recordatorio?`
            );
            if (!email) return;
            await sendRecordatorio(item, email);
            return;
        }
        const confirmMsg = `¿Enviar recordatorio de pago a:\n\n${item.cliente_email}?\n\n${item.cliente_nombre} - ${formatCurrency(item.monto_pendiente)}`;
        if (!window.confirm(confirmMsg)) return;
        await sendRecordatorio(item, null);
    };

    const sendRecordatorio = async (item, overrideEmail) => {
        setEnviandoId(item.servicio_id);
        try {
            const payload = overrideEmail ? { to: overrideEmail } : {};
            const response = await axios.post(
                `${API_URL}/api/servicios/${item.servicio_id}/enviar-recordatorio-pago`,
                payload
            );
            toast.success(`Recordatorio enviado a ${response.data.to}`);
        } catch (error) {
            console.error('Error sending reminder:', error);
            toast.error(error.response?.data?.detail || 'Error al enviar recordatorio');
        } finally {
            setEnviandoId(null);
        }
    };

    const getDiasBadge = (dias) => {
        if (dias > 0) {
            return (
                <Badge className="bg-blue-50 text-blue-700 border-blue-300 border">
                    Vence en {dias} día{dias !== 1 ? 's' : ''}
                </Badge>
            );
        } else if (dias === 0) {
            return (
                <Badge className="bg-amber-50 text-amber-700 border-amber-300 border">
                    Vence HOY
                </Badge>
            );
        } else {
            return (
                <Badge className="bg-rose-50 text-rose-700 border-rose-300 border">
                    Vencido hace {Math.abs(dias)} día{Math.abs(dias) !== 1 ? 's' : ''}
                </Badge>
            );
        }
    };

    const metodoPagoLabel = {
        transferencia: 'Transferencia',
        cheque: 'Cheque',
        efectivo: 'Efectivo',
        tarjeta: 'Tarjeta',
        otro: 'Otro'
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Pagos</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Historial de pagos recibidos y recordatorios pendientes
                    </p>
                </div>
            </div>

            {/* Recordatorios Pendientes */}
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
                                <Mail size={18} className="text-teal-700" />
                            </div>
                            <div>
                                <CardTitle className="text-base">Recordatorios Pendientes de Envío</CardTitle>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    Servicios cuyo vencimiento coincide con tus días configurados:{' '}
                                    <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">
                                        {diasConfig.length > 0 ? diasConfig.join(', ') : '—'}
                                    </code>
                                </p>
                            </div>
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={fetchRecordatorios}
                            disabled={loadingRecord}
                            className="h-8"
                        >
                            <RefreshCw size={14} className={`mr-1 ${loadingRecord ? 'animate-spin' : ''}`} />
                            Actualizar
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {loadingRecord ? (
                        <div className="flex items-center justify-center h-32">
                            <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                        </div>
                    ) : recordatorios.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-32 text-slate-500">
                            <AlertCircle size={32} className="text-slate-300 mb-2" />
                            <p className="text-sm">No hay servicios que cumplan con los días configurados hoy.</p>
                            <p className="text-xs text-slate-400 mt-1">
                                Modifica los días en{' '}
                                <Link to="/configuracion" className="text-teal-700 underline">
                                    Configuración
                                </Link>
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="table-header border-b">
                                        <th className="text-left py-3 px-4">Estado</th>
                                        <th className="text-left py-3 px-4">Cliente</th>
                                        <th className="text-left py-3 px-4">Servicio</th>
                                        <th className="text-right py-3 px-4">Monto Pendiente</th>
                                        <th className="text-center py-3 px-4">Vencimiento</th>
                                        <th className="text-center py-3 px-4">Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recordatorios.map((item) => (
                                        <tr key={item.servicio_id} className="table-row">
                                            <td className="table-cell">
                                                {getDiasBadge(item.dias_diferencia)}
                                            </td>
                                            <td className="table-cell">
                                                <div>
                                                    <p className="font-medium text-slate-900">{item.cliente_nombre}</p>
                                                    {item.cliente_email ? (
                                                        <p className="text-xs text-slate-500">{item.cliente_email}</p>
                                                    ) : (
                                                        <p className="text-xs text-amber-600">⚠ Sin email</p>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="table-cell">
                                                <Link
                                                    to={`/servicios/${item.servicio_id}`}
                                                    className="text-slate-700 hover:text-teal-700 inline-flex items-center gap-1"
                                                >
                                                    <span className="truncate max-w-[200px] inline-block">
                                                        {item.descripcion || '—'}
                                                    </span>
                                                    <ExternalLink size={11} className="opacity-60" />
                                                </Link>
                                                {item.faena && (
                                                    <p className="text-xs text-slate-500 mt-0.5">{item.faena}</p>
                                                )}
                                            </td>
                                            <td className="table-cell text-right">
                                                <span className="font-semibold text-slate-900">
                                                    {formatCurrency(item.monto_pendiente)}
                                                </span>
                                                {item.numero_factura && (
                                                    <p className="text-xs text-slate-500 mt-0.5">
                                                        F° {item.numero_factura}
                                                    </p>
                                                )}
                                            </td>
                                            <td className="table-cell text-center text-sm text-slate-600">
                                                {formatDate(item.fecha_vencimiento)}
                                            </td>
                                            <td className="table-cell text-center">
                                                {canEdit() ? (
                                                    <Button
                                                        size="sm"
                                                        className="bg-teal-700 hover:bg-teal-800 text-white h-8"
                                                        disabled={enviandoId === item.servicio_id}
                                                        onClick={() => handleEnviarRecordatorio(item)}
                                                    >
                                                        {enviandoId === item.servicio_id ? (
                                                            <>
                                                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                                Enviando...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Send size={12} className="mr-1" />
                                                                Enviar
                                                            </>
                                                        )}
                                                    </Button>
                                                ) : (
                                                    <span className="text-xs text-slate-400">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Historial de pagos */}
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                            <CreditCard size={18} className="text-emerald-700" />
                        </div>
                        <CardTitle className="text-base">Historial de Pagos</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center h-48">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5B9A9A]"></div>
                        </div>
                    ) : pagos.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                            <CreditCard size={48} className="text-slate-300 mb-2" />
                            <p>No hay pagos registrados</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="table-header border-b">
                                        <th className="text-left py-3 px-4">Fecha</th>
                                        <th className="text-right py-3 px-4">Monto</th>
                                        <th className="text-center py-3 px-4">Método</th>
                                        <th className="text-center py-3 px-4">Tipo</th>
                                        <th className="text-left py-3 px-4">Observaciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagos.map((pago) => (
                                        <tr key={pago.id} className="table-row">
                                            <td className="table-cell">
                                                <div className="flex items-center gap-2">
                                                    <Calendar size={16} className="text-slate-400" />
                                                    {formatDate(pago.fecha_pago)}
                                                </div>
                                            </td>
                                            <td className="table-cell text-right">
                                                <span className="font-semibold text-emerald-600">
                                                    {formatCurrency(pago.monto)}
                                                </span>
                                            </td>
                                            <td className="table-cell text-center">
                                                <Badge variant="outline">
                                                    {metodoPagoLabel[pago.metodo_pago] || pago.metodo_pago}
                                                </Badge>
                                            </td>
                                            <td className="table-cell text-center">
                                                <Badge className={pago.es_pago_parcial ? 'badge-warning' : 'badge-success'}>
                                                    {pago.es_pago_parcial ? 'Parcial' : 'Total'}
                                                </Badge>
                                            </td>
                                            <td className="table-cell text-slate-600">
                                                {pago.observaciones || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Pagos;
